import { View, Text, ScrollView, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useStudents } from '@/hooks/useStudents';
import { useStudentEngagement, EngagementStatus } from '@/hooks/useStudentEngagement';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { DesktopHome } from '@/components/DesktopHome';

const STATUS_CARD_BORDER = {
  green:  { borderLeftWidth: 4 as const, borderLeftColor: '#2E7D32' },
  yellow: { borderLeftWidth: 4 as const, borderLeftColor: '#F9A825' },
  red:    { borderLeftWidth: 4 as const, borderLeftColor: '#C8102E' },
} satisfies Record<EngagementStatus, object>;

const STATUS_PRIORITY: Record<EngagementStatus, number> = { red: 0, yellow: 1, green: 2 };

const ATTENTION_LIMIT = 5;

export default function PersonalHomeScreen() {
  const { profile } = useAuth();
  const isDesktop = useIsDesktop();
  const { students, loading } = useStudents(profile?.id ?? '');
  const router = useRouter();

  // Em desktop, troca a home mobile por um command center com widgets
  if (isDesktop) return <DesktopHome />;

  const studentIds = students.map(s => s.id);
  const { statuses: engagement, isLoading: engagementLoading } = useStudentEngagement(studentIds);

  const greenCount  = students.filter(s => (engagement[s.id] ?? 'red') === 'green').length;
  const yellowCount = students.filter(s => (engagement[s.id] ?? 'red') === 'yellow').length;
  const redCount    = students.filter(s => (engagement[s.id] ?? 'red') === 'red').length;

  const attentionStudents = [...students]
    .filter(s => {
      const st = engagement[s.id] ?? 'red';
      return st === 'red' || st === 'yellow';
    })
    .sort((a, b) => {
      const pa = STATUS_PRIORITY[engagement[a.id] ?? 'red'];
      const pb = STATUS_PRIORITY[engagement[b.id] ?? 'red'];
      if (pa !== pb) return pa - pb;
      return (a.full_name ?? a.email ?? '').localeCompare(b.full_name ?? b.email ?? '');
    })
    .slice(0, ATTENTION_LIMIT);

  const today = new Date();
  const greeting = today.getHours() < 12 ? 'Bom dia' : today.getHours() < 18 ? 'Boa tarde' : 'Boa noite';

  if (loading || engagementLoading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ padding: 20, paddingBottom: 40 }}>

      {/* Cabeçalho */}
      <Text style={styles.greeting}>{greeting}, {profile?.full_name?.split(' ')[0]}!</Text>

      {/* Card de resumo */}
      <View style={styles.statCard}>
        <Text style={styles.statValue}>{students.length}</Text>
        <Text style={styles.statLabel}>aluno{students.length !== 1 ? 's' : ''} ativo{students.length !== 1 ? 's' : ''}</Text>
        {students.length > 0 && (
          <View style={styles.engagementRow}>
            <View style={styles.engagementItem}>
              <Text style={styles.engagementCount}>{greenCount}</Text>
              <Text style={styles.engagementLabel}>🟢 essa semana</Text>
            </View>
            <View style={styles.engagementDivider} />
            <View style={styles.engagementItem}>
              <Text style={styles.engagementCount}>{yellowCount}</Text>
              <Text style={styles.engagementLabel}>🟡 sem. passada</Text>
            </View>
            <View style={styles.engagementDivider} />
            <View style={styles.engagementItem}>
              <Text style={styles.engagementCount}>{redCount}</Text>
              <Text style={styles.engagementLabel}>🔴 sem treinar</Text>
            </View>
          </View>
        )}
      </View>

      {/* Alunos que precisam de atenção */}
      {attentionStudents.length > 0 && (
        <>
          <View style={styles.sectionRow}>
            <Text style={styles.sectionLabel}>Precisam de atenção</Text>
            <TouchableOpacity onPress={() => router.push('/(personal)/students' as any)}>
              <Text style={styles.seeAll}>Ver todos →</Text>
            </TouchableOpacity>
          </View>

          {attentionStudents.map(student => {
            const status = engagement[student.id] ?? 'red';
            return (
              <TouchableOpacity
                key={student.id}
                style={[styles.studentCard, STATUS_CARD_BORDER[status]]}
                onPress={() => router.push(`/(personal)/students/${student.id}` as any)}
                activeOpacity={0.7}
              >
                <View style={styles.studentAvatar}>
                  <Text style={styles.studentInitial}>
                    {student.full_name?.charAt(0)?.toUpperCase() ?? '?'}
                  </Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.studentName}>{student.full_name ?? student.email}</Text>
                  {student.goal ? <Text style={styles.studentGoal}>{student.goal}</Text> : null}
                </View>
                <Ionicons name="chevron-forward" size={16} color="#ccc" />
              </TouchableOpacity>
            );
          })}
        </>
      )}

      {/* Nenhum aluno ainda */}
      {students.length === 0 && (
        <Text style={styles.empty}>Nenhum aluno cadastrado ainda.</Text>
      )}

      {/* Todos em dia */}
      {students.length > 0 && attentionStudents.length === 0 && (
        <View style={styles.allGoodCard}>
          <Ionicons name="checkmark-circle" size={28} color="#2E7D32" />
          <Text style={styles.allGoodText}>Todos os alunos treinaram essa semana!</Text>
        </View>
      )}

      {/* Ações rápidas */}
      <View style={styles.quickActions}>
        <TouchableOpacity
          style={styles.actionBtn}
          onPress={() => router.push('/(personal)/students' as any)}
          activeOpacity={0.8}
        >
          <Ionicons name="people-outline" size={18} color="#C8102E" />
          <Text style={styles.actionBtnText}>Ver todos os alunos</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.actionBtn}
          onPress={() => router.push('/(personal)/students/new' as any)}
          activeOpacity={0.8}
        >
          <Ionicons name="person-add-outline" size={18} color="#C8102E" />
          <Text style={styles.actionBtnText}>Cadastrar novo aluno</Text>
        </TouchableOpacity>
      </View>

    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  greeting: { fontSize: 22, fontWeight: 'bold', color: '#1A1A1A', marginBottom: 20 },
  statCard: {
    backgroundColor: '#C8102E', borderRadius: 12, padding: 20,
    alignItems: 'center', marginBottom: 20,
  },
  statValue: { fontSize: 40, fontWeight: 'bold', color: '#fff' },
  statLabel: { fontSize: 14, color: 'rgba(255,255,255,0.8)', marginTop: 2 },
  engagementRow: {
    flexDirection: 'row', backgroundColor: 'rgba(0,0,0,0.15)',
    borderRadius: 8, padding: 10, marginTop: 14, justifyContent: 'space-around',
  },
  engagementItem: { alignItems: 'center' },
  engagementCount: { fontSize: 20, fontWeight: 'bold', color: '#fff' },
  engagementLabel: { fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 2 },
  engagementDivider: { width: 1, backgroundColor: 'rgba(255,255,255,0.2)' },
  sectionRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  sectionLabel: { fontSize: 12, fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: 0.8 },
  seeAll: { fontSize: 13, color: '#C8102E', fontWeight: '600' },
  studentCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 8,
    borderWidth: 1.5, borderColor: '#eee',
  },
  studentAvatar: {
    width: 40, height: 40, borderRadius: 20,
    backgroundColor: '#C8102E22', alignItems: 'center', justifyContent: 'center',
  },
  studentInitial: { fontSize: 17, fontWeight: '700', color: '#C8102E' },
  studentName: { fontSize: 15, fontWeight: '600', color: '#1A1A1A' },
  studentGoal: { fontSize: 12, color: '#888', marginTop: 2 },
  allGoodCard: {
    backgroundColor: '#f1f8e9', borderRadius: 12, padding: 20,
    flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16,
  },
  allGoodText: { flex: 1, fontSize: 14, color: '#2E7D32', fontWeight: '600' },
  empty: { textAlign: 'center', color: '#aaa', marginTop: 40 },
  quickActions: { gap: 10, marginTop: 8 },
  actionBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: '#fff', borderRadius: 12, padding: 16,
    borderWidth: 1.5, borderColor: '#eee',
  },
  actionBtnText: { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
});
